import { type ReactNode } from 'react';
import { type RaRecord, useListContext } from 'ra-core';
import { Empty, List, type ListProps } from '../../components/admin/list';
import { ListPagination } from '../../components/admin/list-pagination';
import { cn } from '../../lib/utils';
import './MoonstarList.css';
import { MoonstarListActions } from './MoonstarListActions';
import {
  MoonstarTitle,
  type TitleBreadcrumb,
  useBreadcrumbPageTitle,
} from './MoonstarTitle';

export type MoonstarListProps<TRecord extends RaRecord = any> = Omit<
  ListProps<TRecord>,
  'sort' | 'title' | 'disableBreadcrumb'
> & {
  sort: ListProps<TRecord>['sort'];
  title?: TitleBreadcrumb[];
  /**
   * Rendered instead of the list when there are no records and no filters (default: the kit `Empty`, with a
   * Create button when the resource has a create page). `false` always renders the list.
   */
  empty?: ReactNode | false;
};

export function MoonstarList<TRecord extends RaRecord = any>({
  title,
  actions,
  contentClassName,
  pagination = <MoonstarListPagination />,
  empty = <Empty />,
  children,
  ...props
}: MoonstarListProps<TRecord>) {
  const pageTitle = useBreadcrumbPageTitle(title);

  return (
    <section className="moonstar-list min-w-0">
      <List<TRecord>
        {...props}
        disableBreadcrumb
        title={pageTitle}
        actions={
          actions === false ? (
            false
          ) : (
            <HiddenWhenEmpty empty={empty}>
              {actions ?? <MoonstarListActions />}
            </HiddenWhenEmpty>
          )
        }
        contentClassName={cn('moonstar-list-panel', contentClassName)}
        pagination={pagination}
      >
        <MoonstarTitle breadcrumb={title} />
        <MoonstarListContent empty={empty}>{children}</MoonstarListContent>
      </List>
    </section>
  );
}

function MoonstarListPagination() {
  const { isPending, total } = useListContext();
  if (isPending || total === 0) return null;
  return <ListPagination className="moonstar-list-pagination" />;
}

const useShouldRenderEmpty = (empty: ReactNode | false) => {
  const { isPending, error, data, filterValues } = useListContext();

  return (
    empty !== false &&
    !isPending &&
    !error &&
    data.length === 0 &&
    Object.keys(filterValues).length === 0
  );
};

function MoonstarListContent({
  empty,
  children,
}: {
  empty: ReactNode | false;
  children?: ReactNode;
}) {
  return <>{useShouldRenderEmpty(empty) ? empty : children}</>;
}

/** Like react-admin's empty page, hide the header actions (e.g. Create) while the empty state has its own. */
function HiddenWhenEmpty({
  empty,
  children,
}: {
  empty: ReactNode | false;
  children: ReactNode;
}) {
  return <>{useShouldRenderEmpty(empty) ? null : children}</>;
}
